import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  MessageSquare,
  User,
  Eye,
  MessageCircle,
  Calendar,
  CheckCircle,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import CommonLayout from "../DashboardPage/DashboardMenu";
import { Helmet } from "react-helmet";
import { getAuthToken } from "utils/storage";

interface ChatRequest {
  id: string;
  user: {
    id: number;
    firstName?: string;
    lastName?: string;
    email: string;
    profil?: {
      firstName?: string;
      lastName?: string;
    };
  };
  conversationId: string;
  lastMessage: string;
  userPreferences?: {
    eventType?: string;
    guestCount?: string;
    budget?: string;
    name?: string;
    email?: string;
    phone?: string;
  };
  isRead: boolean;
  createdAt: string;
  status: "pending" | "active" | "resolved";
  messageCount?: number;
}

const AdminChatbotChats: React.FC = () => {
  const [chatRequests, setChatRequests] = useState<ChatRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<"all" | "pending" | "active" | "resolved">("pending");
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchChatRequests();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchChatRequests, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchChatRequests = async () => {
    try {
      if (!loading) setRefreshing(true);

      const response = await fetch("/api/conversations/", {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log("Conversations data:", data);

        // Transform the data to match our interface
        const transformedData =
          data.conversations
            ?.filter((conv: any) => {
              // Filter conversations that came from chatbot (look for specific markers)
              return conv.chats?.some(
                (chat: any) =>
                  chat.content?.includes("chatbot EventToBee") ||
                  chat.content?.includes("Transfert depuis le chatbot")
              );
            })
            .map((conv: any) => {
              const otherUser = conv.user1?.role === "admin" ? conv.user2 : conv.user1;
              const lastChat = conv.chats?.[0];

              return {
                id: conv.id,
                user: otherUser,
                conversationId: conv.id,
                lastMessage: lastChat?.content || "Nouveau chat depuis le chatbot",
                isRead: lastChat?.isRead || false,
                createdAt: conv.createdAt,
                status: getConversationStatus(conv),
                messageCount: conv.chats?.length || 0,
                userPreferences: extractUserPreferences(lastChat?.content),
              };
            }) || [];

        setChatRequests(transformedData);
      }
    } catch (error) {
      console.error("Error fetching chat requests:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const extractUserPreferences = (content: string): any => {
    if (!content) return {};

    const preferences: any = {};

    // Extract information from the formatted chatbot message
    const eventTypeMatch = content.match(/Type d'événement\s*:\s*([^\n]+)/);
    const guestCountMatch = content.match(/Nombre d'invités\s*:\s*([^\n]+)/);
    const budgetMatch = content.match(/Budget\s*:\s*([^\n]+)/);
    const nameMatch = content.match(/Nom\s*:\s*([^\n]+)/);
    const emailMatch = content.match(/Email\s*:\s*([^\n]+)/);
    const phoneMatch = content.match(/Téléphone\s*:\s*([^\n]+)/);

    if (eventTypeMatch && eventTypeMatch[1] !== "Non spécifié") {
      preferences.eventType = eventTypeMatch[1].trim();
    }
    if (guestCountMatch && guestCountMatch[1] !== "Non spécifié") {
      preferences.guestCount = guestCountMatch[1].trim();
    }
    if (budgetMatch && budgetMatch[1] !== "Non spécifié") {
      preferences.budget = budgetMatch[1].trim();
    }
    if (nameMatch && nameMatch[1] !== "Non spécifié") {
      preferences.name = nameMatch[1].trim();
    }
    if (emailMatch && emailMatch[1] !== "Non spécifié") {
      preferences.email = emailMatch[1].trim();
    }
    if (phoneMatch && phoneMatch[1] !== "Non spécifié") {
      preferences.phone = phoneMatch[1].trim();
    }

    return preferences;
  };

  const getConversationStatus = (conversation: any): "pending" | "active" | "resolved" => {
    if (!conversation.chats?.length) return "pending";

    const lastMessage = conversation.chats[0];
    const hoursSinceLastMessage = (Date.now() - new Date(lastMessage.createdAt).getTime()) / (1000 * 60 * 60);

    // If admin has responded, it's active
    const hasAdminResponse = conversation.chats.some(
      (chat: any) => chat.sender_id !== conversation.chats[0].sender_id
    );

    if (hasAdminResponse && hoursSinceLastMessage < 24) return "active";
    if (hasAdminResponse && hoursSinceLastMessage > 48) return "resolved";
    if (hoursSinceLastMessage < 2) return "pending";

    return "pending";
  };

  const filteredChats = chatRequests.filter((chat) => {
    if (activeFilter === "all") return true;
    return chat.status === activeFilter;
  });

  const handleChatClick = (conversationId: string) => {
    // Navigate to the admin messages support view for this conversation
    window.location.href = `/client/messages-support/${conversationId}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "text-orange-600 bg-orange-100 dark:bg-orange-900/20 dark:text-orange-400";
      case "active":
        return "text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-400";
      case "resolved":
        return "text-gray-600 bg-gray-100 dark:bg-gray-900/20 dark:text-gray-400";
      default:
        return "text-gray-600 bg-gray-100 dark:bg-gray-900/20 dark:text-gray-400";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "pending":
        return <AlertCircle className="w-4 h-4" />;
      case "active":
        return <MessageCircle className="w-4 h-4" />;
      case "resolved":
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <MessageCircle className="w-4 h-4" />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "pending":
        return "En attente";
      case "active":
        return "Actif";
      case "resolved":
        return "Résolu";
      default:
        return "Inconnu";
    }
  };

  return (
    <div className="nc-AdminChatbotChats">
      <Helmet>
        <title>Chats Chatbot | Admin EventToBee</title>
      </Helmet>

      <CommonLayout>
        <div className="space-y-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-between"
          >
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                💬 Chats Chatbot
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                Gérez les demandes de chat direct depuis le chatbot EventToBee
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={fetchChatRequests}
                disabled={refreshing}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
                <span>Actualiser</span>
              </motion.button>

              <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-2xl shadow-lg">
                <p className="text-sm font-medium">Demandes en attente</p>
                <p className="text-2xl font-bold">
                  {chatRequests.filter((chat) => chat.status === "pending").length}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Filter Tabs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="flex space-x-2 bg-white/70 dark:bg-gray-800/70 backdrop-blur-lg rounded-2xl p-2 shadow-xl border border-gray-200/50 dark:border-gray-700/50"
          >
            {[
              { key: "all", label: "Tous", count: chatRequests.length },
              {
                key: "pending",
                label: "En attente",
                count: chatRequests.filter((c) => c.status === "pending").length,
              },
              { key: "active", label: "Actifs", count: chatRequests.filter((c) => c.status === "active").length },
              {
                key: "resolved",
                label: "Résolus",
                count: chatRequests.filter((c) => c.status === "resolved").length,
              },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveFilter(tab.key as any)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-all duration-200 ${
                  activeFilter === tab.key
                    ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg"
                    : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                }`}
              >
                <span>{tab.label}</span>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    activeFilter === tab.key
                      ? "bg-white/20 text-white"
                      : "bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400"
                  }`}
                >
                  {tab.count}
                </span>
              </button>
            ))}
          </motion.div>

          {/* Chat Requests List */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-lg rounded-3xl shadow-xl border border-gray-200/50 dark:border-gray-700/50"
          >
            <div className="p-8">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
                </div>
              ) : filteredChats.length === 0 ? (
                <div className="text-center py-12">
                  <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    Aucune demande de chat
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    Les nouvelles demandes de chat depuis le chatbot apparaîtront ici.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredChats.map((chat, index) => (
                    <motion.div
                      key={chat.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      onClick={() => handleChatClick(chat.conversationId)}
                      className="bg-white dark:bg-gray-700 rounded-2xl p-6 border border-gray-200 dark:border-gray-600 hover:shadow-lg transition-all duration-200 cursor-pointer group"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-4 flex-1">
                          <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-semibold">
                            {chat.user?.profil?.firstName?.[0] ||
                              chat.user?.firstName?.[0] ||
                              chat.user?.email?.[0]?.toUpperCase() ||
                              "?"}
                          </div>

                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h3 className="font-semibold text-gray-900 dark:text-white">
                                {chat.user?.profil?.firstName ||
                                  chat.user?.firstName ||
                                  chat.userPreferences?.name ||
                                  chat.user?.email ||
                                  "Utilisateur inconnu"}
                              </h3>
                              <span
                                className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(chat.status)}`}
                              >
                                {getStatusIcon(chat.status)}
                                <span>{getStatusLabel(chat.status)}</span>
                              </span>
                              {chat.messageCount && chat.messageCount > 1 && (
                                <span className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs px-2 py-1 rounded-full">
                                  {chat.messageCount} messages
                                </span>
                              )}
                            </div>

                            <p className="text-gray-600 dark:text-gray-400 text-sm mb-3 line-clamp-2">
                              {chat.lastMessage.length > 100
                                ? chat.lastMessage.substring(0, 100) + "..."
                                : chat.lastMessage}
                            </p>

                            {chat.userPreferences && Object.keys(chat.userPreferences).length > 0 && (
                              <div className="flex flex-wrap gap-2 mb-3">
                                {chat.userPreferences.eventType && (
                                  <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded-lg text-xs">
                                    📅 {chat.userPreferences.eventType}
                                  </span>
                                )}
                                {chat.userPreferences.guestCount && (
                                  <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-lg text-xs">
                                    👥 {chat.userPreferences.guestCount}
                                  </span>
                                )}
                                {chat.userPreferences.budget && (
                                  <span className="px-2 py-1 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded-lg text-xs">
                                    💰 {chat.userPreferences.budget}
                                  </span>
                                )}
                                {chat.userPreferences.email && (
                                  <span className="px-2 py-1 bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300 rounded-lg text-xs">
                                    📧 {chat.userPreferences.email}
                                  </span>
                                )}
                              </div>
                            )}

                            <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                              <div className="flex items-center space-x-1">
                                <Calendar className="w-4 h-4" />
                                <span>
                                  {new Date(chat.createdAt).toLocaleDateString("fr-FR", {
                                    day: "numeric",
                                    month: "short",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <User className="w-4 h-4" />
                                <span>{chat.user?.email}</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors">
                            <Eye className="w-4 h-4" />
                          </div>
                          <div className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors">
                            <MessageCircle className="w-4 h-4" />
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </CommonLayout>
    </div>
  );
};

export default AdminChatbotChats;
